import { useEffect, useState } from "react";
import { Brain, Calendar, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";
import { BaseProgressStats, StatConfig } from "@/core/components/stats";
import { getCurrentUser, getProgressLogs } from "@/lib/api";
import { useProductContext } from "@/contexts/ProductContext";

interface ReliefStats {
  lowStressDays: number;
  avgStressLevel: number;
  mindfulMinutes: number;
}

export const ReliefProgressStats = () => {
  const { t } = useTranslation();
  const { currentProduct } = useProductContext();
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState<ReliefStats>({
    lowStressDays: 0,
    avgStressLevel: 0,
    mindfulMinutes: 0,
  });

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    setIsLoading(true);
    try {
      const userResult = await getCurrentUser();
      if (!userResult.success) return;

      // Get last 30 days of logs
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      const startDate = thirtyDaysAgo.toISOString().split('T')[0];

      const logsResult = await getProgressLogs(userResult.data.id, startDate, undefined, currentProduct);
      if (!logsResult.success || !logsResult.data) return;

      const logs = logsResult.data;
      
      // Calculate low stress days (stress_level <= 3)
      const lowStressDays = logs.filter(log => 
        log.stress_level !== null && log.stress_level !== undefined && log.stress_level <= 3
      ).length;

      // Calculate average stress level
      const stressLogs = logs.filter(log => log.stress_level !== null && log.stress_level !== undefined);
      const avgStress = stressLogs.length > 0 
        ? stressLogs.reduce((sum, log) => sum + (log.stress_level || 0), 0) / stressLogs.length
        : 0;

      // Mindful minutes (simplified: count of mood logs * 5 minutes each)
      const mindfulMinutes = logs.filter(log => log.mood_rating !== null).length * 5;

      setStats({
        lowStressDays,
        avgStressLevel: Math.round(avgStress * 10) / 10,
        mindfulMinutes,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const statConfigs: StatConfig[] = [
    {
      icon: Calendar,
      value: stats.lowStressDays,
      label: t('mindco-relief:progressStats.lowStressDays', 'Low Stress Days'),
    },
    {
      icon: Brain,
      value: stats.avgStressLevel,
      label: t('mindco-relief:progressStats.avgStressLevel', 'Avg Stress Level'),
      suffix: '/10',
    },
    {
      icon: Sparkles,
      value: stats.mindfulMinutes,
      label: t('mindco-relief:progressStats.mindfulMinutes', 'Mindful Minutes'),
    },
  ];

  return (
    <BaseProgressStats 
      stats={statConfigs} 
      isLoading={isLoading}
    />
  );
};
